import React, { useState, useEffect, useRef, useCallback, FC } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Stack,
    Button,
    Divider,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import InventoryTable from './food/InventoryTable';
import FoodEntryForm from './food/FoodEntryForm';
import HungerZoneMap from './food/HungerZoneMap';
import { getFoodInventory, getHungerZones, getNearestHungerZones, transferFood, getLiveDataUpdates, FoodItem, HungerZone } from '../../services/api';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Fix for Leaflet marker icons in React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import locationIcon from 'leaflet/dist/images/marker-icon-red.png';

// Custom icons for markers
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

let LocationIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    className: 'pulse-marker'
});

L.Marker.prototype.options.icon = DefaultIcon;

// Set view to user location component
interface SetViewProps {
    center: [number, number];
    zoom: number;
}

const SetView: React.FC<SetViewProps> = ({ center, zoom }) => {
    const map = useMap();
    map.setView(center, zoom);
    return null;
};

// Define the FoodTransferRequest type
interface FoodTransferRequest {
    hungerZoneId: string;
    foodItems: {
        foodItemId: string;
        quantity: number;
    }[];
    transportInfo: {
        estimatedArrival: string;
        transportMethod: string;
    };
}

// Component definition
const FoodTracker: React.FC = () => {
    // State variables
    const [tabValue, setTabValue] = useState(1); // Default to hunger zones tab
    const [inventory, setInventory] = useState<FoodItem[]>([]);
    const [hungerZones, setHungerZones] = useState<HungerZone[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const [locationLoading, setLocationLoading] = useState(true);
    const [locationError, setLocationError] = useState<string | null>(null);
    const [transferDialogOpen, setTransferDialogOpen] = useState(false);
    const [selectedHungerZone, setSelectedHungerZone] = useState<HungerZone | null>(null);
    const [nearestHungerZones, setNearestHungerZones] = useState<HungerZone[]>([]);
    const [dataLastUpdated, setDataLastUpdated] = useState<Date>(new Date());
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [viewMode, setViewMode] = useState<'live' | 'static'>('live');
    const mapRef = useRef<L.Map | null>(null);

    // Fetch data
    const fetchData = useCallback(async (isRefresh = false) => {
        try {
            if (!isRefresh) {
                setLoading(true);
            } else {
                setIsRefreshing(true);
            }

            // Use the real API endpoints instead of mock data
            if (userLocation && viewMode === 'live') {
                // For live data, use the new live data API endpoint
                const [latitude, longitude] = userLocation;
                try {
                    const liveData = await getLiveDataUpdates(latitude, longitude);

                    // Update data with the response
                    setHungerZones(liveData.hungerZones);
                    if (liveData.inventory) {
                        setInventory(liveData.inventory);
                    } else {
                        // If live data doesn't include inventory, fetch it separately
                        const inventoryData = await getFoodInventory();
                        setInventory(inventoryData);
                    }

                    // Process the hunger zones to calculate distances
                    if (liveData.hungerZones.length > 0) {
                        findNearestHungerZones(userLocation, liveData.hungerZones);
                    }

                    setDataLastUpdated(new Date(liveData.timestamp || Date.now()));
                } catch (error) {
                    console.error('Error fetching live data:', error);
                    // Fallback to separate API calls if live data fails
                    await fetchSeparateData();
                }
            } else {
                // If not using live data or no user location, fetch data separately
                await fetchSeparateData();
            }

            if (!isRefresh) {
                setLoading(false);
            } else {
                setIsRefreshing(false);
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load food tracking data. Please try again later.');
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [userLocation, viewMode]);

    // Helper function to fetch data separately (not using live data endpoint)
    const fetchSeparateData = async () => {
        try {
            // Fetch inventory data from real API
            const inventoryData = await getFoodInventory();
            setInventory(inventoryData.length > 0 ? inventoryData : []);

            // Load hunger zones data
            await loadHungerZonesData();

            setDataLastUpdated(new Date());
        } catch (error) {
            console.error('Error in fetchSeparateData:', error);
            throw error;
        }
    };

    // Separate function for loading hunger zones data
    const loadHungerZonesData = async () => {
        try {
            // If we have user location, get nearest hunger zones from the API
            if (userLocation) {
                const [latitude, longitude] = userLocation;
                try {
                    // Use the real API endpoint for nearest hunger zones
                    const nearestZonesData = await getNearestHungerZones(latitude, longitude, 5);

                    if (nearestZonesData && nearestZonesData.length > 0) {
                        setHungerZones(nearestZonesData);
                        // Calculate distances and find nearest zones
                        findNearestHungerZones(userLocation, nearestZonesData);
                        return;
                    }
                } catch (error) {
                    console.error('Error fetching nearest hunger zones:', error);
                    // If nearest zones fail, fall back to all hunger zones
                }
            }

            // Fallback: fetch all hunger zones if we don't have location or nearest zones failed
            const allZonesData = await getHungerZones();
            if (allZonesData && allZonesData.length > 0) {
                setHungerZones(allZonesData);
                // If we have user location, still calculate distances
                if (userLocation) {
                    findNearestHungerZones(userLocation, allZonesData);
                }
            } else {
                // If API returns no data, use default zones as fallback
                console.warn('No hunger zones data received from API, using fallback data');
                const fallbackHungerZones: HungerZone[] = [
                    {
                        id: '1',
                        region: 'Northern Districts',
                        severity: 8,
                        affectedPopulation: 15000,
                        latitude: 13.1989,
                        longitude: 77.7068,
                        foodNeeds: [
                            { type: 'Rice', quantityNeeded: 5000, unit: 'kg' },
                            { type: 'Lentils', quantityNeeded: 1000, unit: 'kg' }
                        ]
                    },
                    {
                        id: '2',
                        region: 'Coastal Areas',
                        severity: 6,
                        affectedPopulation: 8000,
                        latitude: 13.5500,
                        longitude: 77.3000,
                        foodNeeds: [
                            { type: 'Wheat', quantityNeeded: 3000, unit: 'kg' },
                            { type: 'Milk Powder', quantityNeeded: 500, unit: 'kg' }
                        ]
                    }
                ];
                setHungerZones(fallbackHungerZones);
                if (userLocation) {
                    findNearestHungerZones(userLocation, fallbackHungerZones);
                }
            }
        } catch (err) {
            console.error('Error loading hunger zones:', err);
            setError('Failed to load hunger zone data. Please try again later.');
        }
    };

    // Get user location
    const getUserLocation = () => {
        setLocationLoading(true);
        setLocationError(null);

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setUserLocation([latitude, longitude]);
                    setLocationLoading(false);

                    // Load hunger zones data based on this location
                    loadHungerZonesData();
                },
                (error) => {
                    console.error('Error getting user location:', error);
                    setLocationError('Could not get your location. Using default location.');
                    // Default to Bangalore location
                    setUserLocation([12.9716, 77.5946]);
                    setLocationLoading(false);

                    // Load hunger zones data based on default location
                    loadHungerZonesData();
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 10000 }
            );
        } else {
            setLocationError('Geolocation is not supported by your browser. Using default location.');
            // Default to Bangalore location
            setUserLocation([12.9716, 77.5946]);
            setLocationLoading(false);

            // Load hunger zones data based on default location
            loadHungerZonesData();
        }
    };

    // Request user location on initial load
    useEffect(() => {
        getUserLocation();
    }, []);

    // Find nearest hunger zones
    const findNearestHungerZones = (location: [number, number], zones: HungerZone[]) => {
        const [userLat, userLng] = location;

        // Calculate distances and sort by distance
        const zonesWithDistance = zones.map(zone => {
            // Calculate distance in kilometers using Haversine formula
            const lat1 = userLat * Math.PI / 180;
            const lat2 = zone.latitude * Math.PI / 180;
            const deltaLat = (zone.latitude - userLat) * Math.PI / 180;
            const deltaLng = (zone.longitude - userLng) * Math.PI / 180;

            const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const distance = 6371 * c; // Earth's radius in km * c

            return { ...zone, distance };
        });

        // Sort by distance and severity (weighted)
        const sortedZones = zonesWithDistance.sort((a, b) => {
            // Weight: 60% distance, 40% severity
            const aScore = (a.distance * 0.6) - (a.severity * 0.4);
            const bScore = (b.distance * 0.6) - (b.severity * 0.4);
            return aScore - bScore;
        });

        // Take top 3
        setNearestHungerZones(sortedZones.slice(0, 3));

        // If no zone is selected, select the nearest one automatically
        if (!selectedHungerZone && sortedZones.length > 0) {
            setSelectedHungerZone(sortedZones[0]);
        }
    };

    // Handle tab change
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Handle transfer dialog
    const openTransferDialog = (zone: HungerZone) => {
        setSelectedHungerZone(zone);
        setTransferDialogOpen(true);
    };

    const closeTransferDialog = () => {
        setTransferDialogOpen(false);
    };

    // Add a new food item
    const handleAddFoodItem = (newItem: FoodItem) => {
        setInventory(prev => [...prev, { ...newItem, id: Date.now().toString() }]);
    };

    // Function to handle food transfer - update to use real API
    const handleFoodTransfer = async () => {
        if (!selectedHungerZone) return;

        try {
            // Get selected food items from the dialog (this would need to be implemented)
            // For now, we'll just use a simple example
            const selectedItems = inventory
                .filter(item => selectedHungerZone.foodNeeds.some(need => need.type === item.name))
                .slice(0, 2)
                .map(item => ({
                    foodItemId: item.id,
                    quantity: Math.min(item.quantity, 100) // Just transfer a portion as an example
                }));

            if (selectedItems.length === 0) {
                alert('No matching food items available to transfer');
                return;
            }

            // Create the transfer request
            const transferRequest: FoodTransferRequest = {
                hungerZoneId: selectedHungerZone.id,
                foodItems: selectedItems,
                transportInfo: {
                    estimatedArrival: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
                    transportMethod: 'Truck',
                }
            };

            // Call the API
            const response = await transferFood(transferRequest);

            if (response.success) {
                alert(`Food transfer initiated to ${selectedHungerZone.region}. Transfer ID: ${response.transferId}`);

                // Update inventory after successful transfer
                setInventory(prev => prev.map(item => {
                    const transferredItem = transferRequest.foodItems.find(i => i.foodItemId === item.id);
                    if (transferredItem) {
                        return {
                            ...item,
                            quantity: item.quantity - transferredItem.quantity
                        };
                    }
                    return item;
                }));
            } else {
                alert(`Transfer failed: ${response.message}`);
            }

            closeTransferDialog();
        } catch (error) {
            console.error('Error during food transfer:', error);
            alert('Failed to transfer food. Please try again later.');
        }
    };

    // Handle refresh button click
    const handleRefresh = () => {
        fetchData(true);
    };

    // Toggle live/static data mode
    const toggleViewMode = () => {
        setViewMode(prev => prev === 'live' ? 'static' : 'live');
    };

    // Handle retry location
    const handleRetryLocation = () => {
        getUserLocation();
    };

    // Initial data load
    useEffect(() => {
        fetchData();

        // Set up auto-refresh for live data every 30 seconds
        const refreshInterval = setInterval(() => {
            if (viewMode === 'live') {
                fetchData(true);
            }
        }, 30000);

        return () => clearInterval(refreshInterval);
    }, [fetchData, viewMode]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h6" sx={{ mt: 3 }}>
                    Loading Food Tracking Data...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="contained" onClick={() => fetchData()}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Paper sx={{ p: 2, height: '100%' }}>
            {/* Location Status Bar */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                p: 1,
                bgcolor: 'background.paper',
                borderRadius: 1,
                boxShadow: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <MyLocationIcon color="primary" sx={{ mr: 1 }} />

                    {locationLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            <Typography variant="body2">
                                Locating you...
                            </Typography>
                        </Box>
                    ) : locationError ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="error" sx={{ mr: 1 }}>
                                {locationError}
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleRetryLocation}
                            >
                                Retry
                            </Button>
                        </Box>
                    ) : userLocation ? (
                        <Typography variant="body2">
                            Your Location: {userLocation[0].toFixed(4)}, {userLocation[1].toFixed(4)}
                            {nearestHungerZones.length > 0 && (
                                <span> • Nearest shortage: <b>{nearestHungerZones[0]?.region}</b> ({Math.round(nearestHungerZones[0]?.distance || 0)} km)</span>
                            )}
                        </Typography>
                    ) : (
                        <Typography variant="body2">
                            Unable to determine your location
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                        <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                            Last updated: {dataLastUpdated.toLocaleTimeString()}
                        </Typography>
                    </Box>

                    <Button
                        size="small"
                        variant={viewMode === 'live' ? 'contained' : 'outlined'}
                        color={viewMode === 'live' ? 'success' : 'primary'}
                        onClick={toggleViewMode}
                        startIcon={viewMode === 'live' ? <WarningIcon /> : null}
                        sx={{
                            mr: 1,
                            borderRadius: 8,       // Rounded edges to match the theme
                            textTransform: 'none', // Disable uppercase transformation
                            minHeight: 48,         // Ensures comfortable touch/click area
                            px: 2,                 // Horizontal padding for balance
                        }}
                    >
                        {viewMode === 'live' ? 'Live Data' : 'Static Data'}
                    </Button>

                    <IconButton
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        color="primary"
                        size="small"
                    >
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {isRefreshing && (
                <LinearProgress sx={{ mb: 2 }} />
            )}

            <Typography variant="h5" gutterBottom>
                Food Inventory & Hunger Zone Tracking
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="food tracking tabs">
                    <Tab label="Inventory" />
                    <Tab label="Hunger Zones Map" />
                    <Tab label="Add Food Items" />
                    <Tab label="Food Transfer" />
                </Tabs>
            </Box>

            {/* Live data alert */}
            {viewMode === 'live' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        Live data mode is enabled. Information updates automatically every 30 seconds based on your location.
                    </Typography>
                </Alert>
            )}

            {/* Inventory Tab */}
            <Box role="tabpanel" hidden={tabValue !== 0}>
                {tabValue === 0 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Current Food Inventory
                        </Typography>
                        <InventoryTable inventory={inventory} />

                        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                            Expiring Soon (within 30 days)
                        </Typography>
                        <Stack spacing={1}>
                            {inventory
                                .filter(item => {
                                    const daysUntilExpiry = Math.floor((new Date(item.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                                    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                                })
                                .map(item => {
                                    const daysUntilExpiry = Math.floor((new Date(item.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                                    return (
                                        <Paper key={item.id} sx={{ p: 2 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Typography variant="subtitle1">{item.name}</Typography>
                                                    <Typography variant="body2">
                                                        {item.quantity} {item.unit} - Expires in {daysUntilExpiry} days
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => {
                                                        const matchingZone = hungerZones.find(zone =>
                                                            zone.foodNeeds.some(need => need.type === item.name)
                                                        );
                                                        if (matchingZone) {
                                                            openTransferDialog(matchingZone);
                                                        } else if (hungerZones.length > 0) {
                                                            openTransferDialog(hungerZones[0]);
                                                        }
                                                    }}
                                                >
                                                    Transfer
                                                </Button>
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                            {inventory.filter(item => {
                                const daysUntilExpiry = Math.floor((new Date(item.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                                return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                            }).length === 0 && (
                                    <Typography variant="body2">No items expiring soon.</Typography>
                                )}
                        </Stack>
                    </Box>
                )}
            </Box>

            {/* Hunger Zones Tab */}
            <Box role="tabpanel" hidden={tabValue !== 1}>
                {tabValue === 1 && (
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6">
                                Hunger Zones Map
                            </Typography>

                            {nearestHungerZones.length > 0 && (
                                <Alert severity="warning" icon={false} sx={{ py: 0 }}>
                                    <Typography variant="body2">
                                        <b>Nearest critical zone:</b> {nearestHungerZones[0]?.region} ({Math.round(nearestHungerZones[0]?.distance || 0)} km)
                                        <Button
                                            size="small"
                                            sx={{ ml: 1 }}
                                            variant="outlined"
                                            color="warning"
                                            onClick={() => openTransferDialog(nearestHungerZones[0])}
                                        >
                                            Help Now
                                        </Button>
                                    </Typography>
                                </Alert>
                            )}
                        </Stack>

                        <HungerZoneMap
                            hungerZones={hungerZones}
                            userLocation={userLocation}
                            onZoneSelect={openTransferDialog}
                        />
                    </Box>
                )}
            </Box>

            {/* Add Food Items Tab */}
            <Box role="tabpanel" hidden={tabValue !== 2}>
                {tabValue === 2 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Add New Food Items
                        </Typography>
                        <FoodEntryForm onAddFood={handleAddFoodItem} />
                    </Box>
                )}
            </Box>

            {/* Food Transfer Tab */}
            <Box role="tabpanel" hidden={tabValue !== 3}>
                {tabValue === 3 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Nearest Hunger Zones
                        </Typography>
                        <Typography variant="body2" paragraph>
                            These are the areas most in need of food assistance based on your location, proximity and severity.
                        </Typography>

                        <Stack spacing={2}>
                            {nearestHungerZones.map((zone, index) => (
                                <Paper key={zone.id} sx={{ p: 2, borderLeft: 6, borderColor: index === 0 ? 'error.main' : index === 1 ? 'warning.main' : 'info.main' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="subtitle1">{zone.region}</Typography>
                                                <Chip
                                                    label={`Severity: ${zone.severity.toFixed(1)}/10`}
                                                    color={zone.severity >= 8 ? "error" : zone.severity >= 5 ? "warning" : "success"}
                                                    size="small"
                                                />
                                                {index === 0 && (
                                                    <Chip color="error" size="small" label="NEAREST" />
                                                )}
                                            </Stack>
                                            <Typography variant="body2">
                                                Affected population: {zone.affectedPopulation.toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                Distance: {Math.round(zone.distance || 0)} km from your location
                                            </Typography>
                                            <Box sx={{ mt: 1 }}>
                                                <Typography variant="body2" fontWeight="bold">Urgent needs:</Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    {zone.foodNeeds.map((need, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={`${need.type}: ${need.quantityNeeded} ${need.unit}`}
                                                            size="small"
                                                            variant="outlined"
                                                            color={index === 0 ? "error" : "default"}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            color={index === 0 ? "error" : "primary"}
                                            onClick={() => openTransferDialog(zone)}
                                        >
                                            Transfer Food
                                        </Button>
                                    </Stack>
                                </Paper>
                            ))}

                            {nearestHungerZones.length === 0 && (
                                <Alert severity="info">
                                    No hunger zones found near your location. Try refreshing the data.
                                </Alert>
                            )}
                        </Stack>
                    </Box>
                )}
            </Box>

            {/* Transfer Dialog */}
            <Dialog open={transferDialogOpen} onClose={closeTransferDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    Transfer Food to {selectedHungerZone?.region}
                </DialogTitle>
                <DialogContent>
                    {selectedHungerZone && (
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Zone Details:
                            </Typography>
                            <Typography variant="body2">
                                Severity: {selectedHungerZone.severity.toFixed(1)}/10
                            </Typography>
                            <Typography variant="body2">
                                Affected Population: {selectedHungerZone.affectedPopulation.toLocaleString()}
                            </Typography>
                            <Typography variant="body2">
                                Distance from your location: {Math.round(selectedHungerZone.distance || 0)} km
                            </Typography>

                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                                Food Needs:
                            </Typography>
                            <Stack spacing={1}>
                                {selectedHungerZone.foodNeeds.map((need, idx) => (
                                    <Box key={idx}>
                                        <Typography variant="body2">
                                            {need.type}: {need.quantityNeeded} {need.unit}
                                        </Typography>

                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2">
                                                Available from your inventory:
                                            </Typography>
                                            {inventory
                                                .filter(item => item.name === need.type)
                                                .map(item => (
                                                    <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Typography>
                                                            {item.quantity} {item.unit} (Expires: {new Date(item.expiryDate).toLocaleDateString()})
                                                        </Typography>
                                                        <Button variant="outlined" size="small">
                                                            Select
                                                        </Button>
                                                    </Box>
                                                ))}

                                            {inventory.filter(item => item.name === need.type).length === 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    No matching items in inventory
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1">
                                    Transport Information:
                                </Typography>
                                <Typography variant="body2">
                                    Estimated distance: {Math.round(selectedHungerZone.distance || 0)} km
                                </Typography>
                                <Typography variant="body2">
                                    Estimated transit time: {Math.round((selectedHungerZone.distance || 0) / 40 * 60)} minutes
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeTransferDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleFoodTransfer}
                    >
                        Confirm Transfer
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default FoodTracker; 